import React, { type FC, useRef, useState } from "react";
import useOnClickOutside from "../../hooks/useOnClickOutside";

interface DropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  title: string;
  toggleImage?: React.ReactNode;
  indicator?: boolean;
  showAll?: string;
  toggleClassNames?: string;
  menuClassNames?: string;
  indicatorClasses?: string;
  orientation?: "start" | "end";
  shape?: "straight" | "rounded" | "smooth" | "curved";
  onToggle?: () => void;
  onClose?: () => void;
  closeOnSelect?: boolean;
  toggleAriaLabel?: string;
}

const Dropdown: FC<DropdownProps> = ({
  className: classes = "",
  children,
  title,
  toggleImage,
  indicator = true,
  showAll,
  toggleClassNames = "",
  menuClassNames = "",
  indicatorClasses = "",
  orientation = "start",
  shape = "smooth",
  onToggle,
  onClose,
  closeOnSelect = false,
  toggleAriaLabel,
}) => {
  const [show, setShow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(dropdownRef, () => {
    if (show) {
      setShow(false);
      if (onClose) {
        onClose(); // Trigger onClose only when dropdown is open
      }
    }
  });
  
  const handleToggle = () => {
    setShow(!show);
    if (show && onClose) {
      onClose(); // Trigger onClose when dropdown closes
    } else if (!show && onToggle) {
      onToggle(); // Trigger onToggle when dropdown opens
    }
  };

  const handleItemsClick = () => {
    if (!closeOnSelect) return;
    setShow(false);
    onClose?.();
  };

  return (
    <div
      ref={dropdownRef}
      className={`group relative text-start ${classes} ${show ? "active" : ""}`}
    >
      {indicator ? (
        <span
          className={`absolute end-1 top-1 z-[2] block h-2.5 w-2.5 rounded-full border-2 border-white bg-secondary ${indicatorClasses}`}
        ></span>
      ) : null}
      <button
        type="button"
        onClick={handleToggle}
        aria-label={toggleAriaLabel}
        aria-haspopup="menu"
        aria-expanded={show}
        className={`flex h-11 w-11 cursor-pointer items-center justify-center rounded-2xl border border-black/5 bg-white text-lightGray shadow-sm transition-all duration-300 hover:border-secondary/30 hover:text-secondary hover:shadow-md ${toggleClassNames}`}
      >
        {toggleImage}
      </button>
      <div
        className={`before:content-[' '] after:content-[' '] absolute top-[4rem] z-[5] w-[320px] rounded-2xl border border-black/10 bg-white shadow-[0_24px_70px_rgba(17,24,22,0.18)] transition-all duration-300 before:pointer-events-none before:absolute before:bottom-full before:-ms-3 before:h-0 before:w-0 before:border-[12px] before:border-transparent before:border-b-black/10 after:pointer-events-none after:absolute after:bottom-full after:-ms-[11px] after:h-0 after:w-0 after:border-[11px] after:border-transparent after:border-b-white 
          ${
            show
              ? "pointer-events-auto translate-y-0 opacity-100"
              : "pointer-events-none translate-y-[5px] opacity-0"
          }
          ${orientation === "start" ? "-start-3 before:end-[85.5%] after:end-[86%]" : ""}
          ${orientation === "end" ? "sm:-end-3 -end-7 before:start-[90%] after:start-[90%]" : ""}
          ${shape === "rounded" ? "rounded-md" : ""}
          ${shape === "smooth" ? "rounded-lg" : ""}
          ${shape === "curved" ? "rounded-xl" : ""}
          ${menuClassNames}
        `}
      >
        <div className="relative h-full w-full">
          {title && (
            <div className="flex items-center justify-between px-5 py-5 pb-1">
              <div className="font-sans text-xs font-bold uppercase text-lightGray">
                <span>{title}</span>
              </div>
              {showAll && (
                <a
                  href={showAll}
                  className="cursor-pointer font-sans text-xs font-semibold text-secondary underline-offset-4 hover:underline"
                >
                  View all
                </a>
              )}
            </div>
          )}
          <div
            className={`${title.toLowerCase()}-items py-3`}
            onClick={handleItemsClick}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dropdown;
